import React, { useId, useLayoutEffect, useRef, useState } from 'react';

const RANGE_YEARS = { '1Y': 1, '3Y': 3, '5Y': 5 };
const COLORS = ['#153f78', '#11998e', '#d4853f', '#6d5eb5', '#5e748d'];
const CHART_WIDTH = 760;
const CHART_HEIGHT = 310;
const BASE_HIT_RADIUS = 12;
const MIN_HIT_SIZE = 32;
const COLLISION_DISTANCE = 24;
const HIT_REGION_GAP = 1;
const TOOLTIP_GAP = 12;
const TOOLTIP_INSET = 4;

const optionalDetailFields = [
    ['model', 'Model'],
    ['company', 'Company'],
    ['organization', 'Organization'],
    ['provider', 'Provider'],
    ['chip', 'Chip'],
];

export const getTooltipDetails = (point, seriesLabel, valueFormatter) => {
    const details = [];
    if (seriesLabel) details.push({ label: 'Series', value: seriesLabel.replaceAll('_', ' ') });
    optionalDetailFields.forEach(([field, label]) => {
        if (point[field] !== undefined && point[field] !== null && point[field] !== '') {
            details.push({ label, value: String(point[field]) });
        }
    });
    if (Number.isFinite(point.passRate)) details.push({ label: 'Pass rate', value: `${point.passRate}%` });
    if (Number.isFinite(point.low) && Number.isFinite(point.high)) {
        details.push({ label: 'Range', value: `${valueFormatter(point.low)} – ${valueFormatter(point.high)}` });
    }
    if (Number.isFinite(point.benchmarkTotalCost)) {
        details.push({ label: 'Benchmark cost', value: `$${point.benchmarkTotalCost}` });
    }
    return details;
};

export const getPointAriaLabel = (point, seriesLabel, valueFormatter) => [
    point.date,
    valueFormatter(point.value),
    seriesLabel ? seriesLabel.replaceAll('_', ' ') : null,
    point.model,
    point.company,
    point.organization,
    point.provider,
    point.chip,
].filter(Boolean).join(', ');

const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

export const getContainedTooltipPosition = ({
    anchorX,
    anchorY,
    tooltipWidth,
    tooltipHeight,
    containerWidth,
    containerHeight,
    gap = TOOLTIP_GAP,
    inset = TOOLTIP_INSET,
}) => {
    const maxLeft = Math.max(inset, containerWidth - tooltipWidth - inset);
    const maxTop = Math.max(inset, containerHeight - tooltipHeight - inset);
    const aboveTop = anchorY - gap - tooltipHeight;
    const belowTop = anchorY + gap;
    let top;

    if (aboveTop >= inset) {
        top = aboveTop;
    } else if (belowTop + tooltipHeight <= containerHeight - inset) {
        top = belowTop;
    } else {
        const preferredTop = anchorY >= containerHeight / 2 ? aboveTop : belowTop;
        top = clamp(preferredTop, inset, maxTop);
    }

    return {
        left: clamp(anchorX - tooltipWidth / 2, inset, maxLeft),
        top,
    };
};

const getHitRadius = (renderedWidth) => {
    const scale = renderedWidth > 0 ? renderedWidth / CHART_WIDTH : 1;
    return Math.max(BASE_HIT_RADIUS, (MIN_HIT_SIZE / 2) / scale);
};

const clusterRenderedPoints = (renderedPoints, collisionDistance) => {
    const remaining = new Set(renderedPoints.map((_, index) => index));
    const clusters = [];

    while (remaining.size) {
        const first = remaining.values().next().value;
        const memberIndexes = [first];
        remaining.delete(first);
        [...remaining].forEach((candidateIndex) => {
            const candidate = renderedPoints[candidateIndex];
            const touchesEveryMember = memberIndexes.every((memberIndex) => {
                const member = renderedPoints[memberIndex];
                return Math.hypot(candidate.x - member.x, candidate.y - member.y) <= collisionDistance;
            });
            if (touchesEveryMember) {
                memberIndexes.push(candidateIndex);
                remaining.delete(candidateIndex);
            }
        });

        const members = memberIndexes.map((index) => renderedPoints[index]);
        clusters.push({
            id: members.map((member) => member.id).join('|'),
            members,
            x: members.reduce((sum, member) => sum + member.x, 0) / members.length,
            y: members.reduce((sum, member) => sum + member.y, 0) / members.length,
            color: members[0].color,
        });
    }

    return clusters;
};

const sizeClusterHitRegions = (clusters, desiredRadius, scale) => clusters.map((cluster, clusterIndex) => {
    const hitAreas = cluster.members.map((member) => {
        const nearestExternalMemberDistance = clusters.reduce((nearestClusterMember, candidate, candidateIndex) => {
            if (candidateIndex === clusterIndex) return nearestClusterMember;
            return candidate.members.reduce((nearestMember, externalMember) => Math.min(
                nearestMember,
                Math.hypot(externalMember.x - member.x, externalMember.y - member.y)
            ), nearestClusterMember);
        }, Infinity);
        const nonIntersectingRadius = Number.isFinite(nearestExternalMemberDistance)
            ? Math.max(Number.EPSILON, nearestExternalMemberDistance / 2 - HIT_REGION_GAP / scale)
            : desiredRadius;
        return {
            id: member.id,
            x: member.x,
            y: member.y,
            radius: Math.min(desiredRadius, nonIntersectingRadius),
        };
    });

    return {
        ...cluster,
        hitAreas,
    };
});

const getClusterMemberIdentity = ({ point, seriesLabel }) => [...new Set([
    seriesLabel ? seriesLabel.replaceAll('_', ' ') : null,
    point.model,
    point.company,
    point.organization,
    point.provider,
    point.chip,
].filter(Boolean).map(String))].join(' · ') || 'Observation';

export const filterSeriesByRange = (series = [], range = 'All', asOf) => {
    const valid = series.filter((point) => (
        point
        && Number.isFinite(point.value)
        && !Number.isNaN(new Date(`${point.date}T00:00:00`).getTime())
    ));
    if (range === 'All' || !RANGE_YEARS[range]) return valid;
    const cutoff = new Date(`${asOf}T00:00:00`);
    cutoff.setFullYear(cutoff.getFullYear() - RANGE_YEARS[range]);
    return valid.filter((point) => new Date(`${point.date}T00:00:00`) >= cutoff);
};

const formatCompact = (value) => new Intl.NumberFormat('en-US', {
    notation: Math.abs(value) >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: Math.abs(value) < 10 ? 1 : 0,
}).format(value);

const TimeSeriesChart = ({
    dataset,
    range = 'All',
    valueFormatter = formatCompact,
    seriesKey,
    seriesLabelFormatter = (key) => key.replaceAll('_', ' '),
    dashedSeriesKeys = [],
    ariaLabel = 'Time series',
    scale = 'linear',
    zeroBaseline = false,
}) => {
    const tooltipId = useId();
    const chartRef = useRef(null);
    const plotRef = useRef(null);
    const svgRef = useRef(null);
    const tooltipRef = useRef(null);
    const [activeClusterId, setActiveClusterId] = useState(null);
    const [activeDate, setActiveDate] = useState(null);
    const [renderedSvgWidth, setRenderedSvgWidth] = useState(CHART_WIDTH);
    const [tooltipPosition, setTooltipPosition] = useState(null);
    const points = filterSeriesByRange(dataset?.series, range, dataset?.asOf);

    useLayoutEffect(() => {
        const svg = svgRef.current;
        if (!svg) return undefined;

        const measureSvg = () => {
            const nextWidth = svg.getBoundingClientRect().width;
            if (nextWidth > 0) {
                setRenderedSvgWidth((current) => Math.abs(current - nextWidth) < 0.1 ? current : nextWidth);
            }
        };
        measureSvg();

        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measureSvg);
        observer?.observe(svg);
        window.addEventListener('resize', measureSvg);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measureSvg);
        };
    }, [points.length]);

    const width = CHART_WIDTH;
    const height = CHART_HEIGHT;
    const padding = { top: 22, right: 18, bottom: 44, left: 62 };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;
    const dates = points.map((point) => new Date(`${point.date}T00:00:00`).getTime());
    const minDate = Math.min(...dates);
    const maxDate = Math.max(...dates);
    const transform = scale === 'log' ? (value) => Math.log10(Math.max(value, 0.001)) : (value) => value;
    const transformed = points.map((point) => transform(point.value));
    const rawMin = Math.min(...transformed);
    const rawMax = Math.max(...transformed);
    const valuePadding = (rawMax - rawMin || Math.abs(rawMax) || 1) * 0.1;
    const minValue = zeroBaseline && rawMin >= 0 ? 0 : rawMin - valuePadding;
    const maxValue = rawMax + valuePadding;
    const x = (date) => padding.left + ((date - minDate) / (maxDate - minDate || 1)) * plotWidth;
    const y = (value) => padding.top + (1 - ((transform(value) - minValue) / (maxValue - minValue || 1))) * plotHeight;
    const groups = new Map();
    points.forEach((point) => {
        const key = seriesKey ? String(point[seriesKey] || 'Other') : 'Series';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(point);
    });
    const yTicks = Array.from({ length: 4 }, (_, index) => {
        const ratio = index / 3;
        const transformedValue = maxValue - ratio * (maxValue - minValue);
        return scale === 'log' ? 10 ** transformedValue : transformedValue;
    });
    const xTicks = Array.from({ length: 4 }, (_, index) => minDate + (index / 3) * (maxDate - minDate));
    const renderedGroups = [...groups.entries()].map(([key, values], index) => {
        const color = COLORS[index % COLORS.length];
        const dashed = dashedSeriesKeys.includes(key);
        const sorted = [...values].sort((a, b) => a.date.localeCompare(b.date));
        const renderedPoints = sorted.map((point) => {
            const pointX = x(new Date(`${point.date}T00:00:00`).getTime());
            const pointY = y(point.value);
            return {
                id: `${key}-${point.date}-${point.value}`,
                point,
                seriesLabel: seriesKey ? seriesLabelFormatter(key) : '',
                color,
                x: pointX,
                y: pointY,
            };
        });
        return {
            key,
            color,
            dashed,
            renderedPoints,
            path: renderedPoints.map((point, pointIndex) => (
                `${pointIndex ? 'L' : 'M'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`
            )).join(' '),
            last: renderedPoints[renderedPoints.length - 1],
        };
    });
    const renderedScale = renderedSvgWidth / CHART_WIDTH;
    const hitRadius = getHitRadius(renderedSvgWidth);
    const clusters = sizeClusterHitRegions(
        clusterRenderedPoints(
            renderedGroups.flatMap((group) => group.renderedPoints),
            COLLISION_DISTANCE / renderedScale
        ),
        hitRadius,
        renderedScale
    );
    const dateGroups = [...renderedGroups
        .flatMap((group) => group.renderedPoints)
        .reduce((byDate, renderedPoint) => {
            const date = renderedPoint.point.date;
            if (!byDate.has(date)) byDate.set(date, []);
            byDate.get(date).push(renderedPoint);
            return byDate;
        }, new Map())]
        .map(([date, members]) => ({
            id: `date-${date}`,
            date,
            members,
            x: members[0].x,
            y: members.reduce((sum, member) => sum + member.y, 0) / members.length,
            color: members[0].color,
        }))
        .sort((left, right) => left.date.localeCompare(right.date));
    const clusterSignature = clusters.map((cluster) => cluster.id).join('||');
    const activeCluster = clusters.find((cluster) => cluster.id === activeClusterId) || null;
    const activeDateGroup = dateGroups.find((group) => group.date === activeDate) || null;
    const activeSelection = activeDateGroup || activeCluster;
    const activeMemberIds = new Set(activeSelection?.members.map((member) => member.id) || []);

    useLayoutEffect(() => {
        if (activeClusterId && !activeCluster) {
            setActiveClusterId(null);
            setTooltipPosition(null);
        }
    }, [activeCluster, activeClusterId, clusterSignature]);

    useLayoutEffect(() => {
        if (activeDate && !activeDateGroup) {
            setActiveDate(null);
            setTooltipPosition(null);
        }
    }, [activeDate, activeDateGroup]);

    useLayoutEffect(() => {
        if (!activeSelection) return undefined;
        const chart = chartRef.current;
        const plot = plotRef.current;
        const svg = svgRef.current;
        const tooltip = tooltipRef.current;
        if (!chart || !plot || !svg || !tooltip) return undefined;

        const measureTooltip = () => {
            const chartRect = chart.getBoundingClientRect();
            const plotRect = plot.getBoundingClientRect();
            const svgRect = svg.getBoundingClientRect();
            const tooltipRect = tooltip.getBoundingClientRect();
            const visibleWidth = chart.clientWidth || chartRect.width;
            const visibleLeft = Math.max(0, chartRect.left - plotRect.left);
            const visibleRight = Math.min(plotRect.width, visibleLeft + visibleWidth);
            const visibleTop = svgRect.top - plotRect.top;
            const containerWidth = visibleRight - visibleLeft;
            const containerHeight = svgRect.height;
            if (containerWidth <= 0 || containerHeight <= 0 || tooltipRect.width <= 0 || tooltipRect.height <= 0) return;

            const anchorX = (svgRect.left - plotRect.left)
                + (activeSelection.x / CHART_WIDTH) * svgRect.width
                - visibleLeft;
            const anchorY = (activeSelection.y / CHART_HEIGHT) * svgRect.height;
            const contained = getContainedTooltipPosition({
                anchorX,
                anchorY,
                tooltipWidth: tooltipRect.width,
                tooltipHeight: tooltipRect.height,
                containerWidth,
                containerHeight,
            });
            const nextPosition = {
                left: visibleLeft + contained.left,
                top: visibleTop + contained.top,
            };
            setTooltipPosition((current) => (
                current?.left === nextPosition.left && current?.top === nextPosition.top
                    ? current
                    : nextPosition
            ));
        };

        measureTooltip();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measureTooltip);
        observer?.observe(svg);
        observer?.observe(tooltip);
        chart.addEventListener('scroll', measureTooltip, { passive: true });
        window.addEventListener('resize', measureTooltip);
        return () => {
            observer?.disconnect();
            chart.removeEventListener('scroll', measureTooltip);
            window.removeEventListener('resize', measureTooltip);
        };
    }, [activeSelection, renderedSvgWidth]);

    if (!points.length) {
        return <div className="ai-dev-chart-empty">No observations in this range</div>;
    }

    return (
        <div className="ai-dev-chart" ref={chartRef}>
            {seriesKey && (
                <div className="ai-dev-chart__legend" aria-label="Legend">
                    {[...groups.keys()].map((key, index) => (
                        <span key={key}>
                            <i
                                className={dashedSeriesKeys.includes(key) ? 'is-dashed' : undefined}
                                style={dashedSeriesKeys.includes(key)
                                    ? { borderTopColor: COLORS[index % COLORS.length] }
                                    : { background: COLORS[index % COLORS.length] }}
                            />
                            {seriesLabelFormatter(key)}
                        </span>
                    ))}
                </div>
            )}
            <div
                className="ai-dev-chart__plot"
                data-testid="ai-dev-chart-plot"
                ref={plotRef}
                onMouseMove={(event) => {
                    const svg = svgRef.current;
                    if (!svg || !dateGroups.length) return;
                    const rect = svg.getBoundingClientRect();
                    if (!rect.width || !rect.height) return;
                    const pointerX = ((event.clientX - rect.left) / rect.width) * CHART_WIDTH;
                    const pointerY = ((event.clientY - rect.top) / rect.height) * CHART_HEIGHT;
                    const insidePlot = pointerX >= padding.left
                        && pointerX <= width - padding.right
                        && pointerY >= padding.top
                        && pointerY <= padding.top + plotHeight;
                    if (!insidePlot) {
                        setActiveDate(null);
                        return;
                    }
                    const nearest = dateGroups.reduce((closest, group) => (
                        Math.abs(group.x - pointerX) < Math.abs(closest.x - pointerX) ? group : closest
                    ), dateGroups[0]);
                    setActiveClusterId(null);
                    setTooltipPosition(null);
                    setActiveDate((current) => current === nearest.date ? current : nearest.date);
                }}
                onPointerLeave={(event) => {
                    if (!event.pointerType || event.pointerType === 'mouse') {
                        setActiveClusterId(null);
                        setActiveDate(null);
                    }
                }}
                onMouseLeave={() => {
                    setActiveClusterId(null);
                    setActiveDate(null);
                }}
            >
                <svg ref={svgRef} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
                    <title>{ariaLabel}</title>
                    <desc>{`${points.length} dated source observations from ${points[0].date} to ${points[points.length - 1].date}.`}</desc>
                    {yTicks.map((tick, index) => {
                        const tickY = padding.top + (index / 3) * plotHeight;
                        return (
                            <g key={`y-${index}`}>
                                <line x1={padding.left} x2={width - padding.right} y1={tickY} y2={tickY} className="ai-dev-chart__grid" />
                                <text x={padding.left - 12} y={tickY + 4} textAnchor="end" className="ai-dev-chart__tick">{valueFormatter(tick)}</text>
                            </g>
                        );
                    })}
                    {xTicks.map((tick, index) => {
                        const tickX = padding.left + (index / 3) * plotWidth;
                        return (
                            <text key={`x-${index}`} x={tickX} y={height - 14} textAnchor={index === 0 ? 'start' : index === 3 ? 'end' : 'middle'} className="ai-dev-chart__tick">
                                {new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short' }).format(new Date(tick))}
                            </text>
                        );
                    })}
                    {renderedGroups.map((group) => (
                        <g key={group.key}>
                            <path
                                d={group.path}
                                fill="none"
                                stroke={group.color}
                                className={`ai-dev-chart__line${group.dashed ? ' is-dashed' : ''}`}
                            />
                            {group.renderedPoints.map((renderedPoint) => (
                                <g key={renderedPoint.id}>
                                    <circle cx={renderedPoint.x} cy={renderedPoint.y} r="3.2" fill={group.color} />
                                    {activeMemberIds.has(renderedPoint.id) ? (
                                        <circle
                                            cx={renderedPoint.x}
                                            cy={renderedPoint.y}
                                            r="6"
                                            className="ai-dev-chart__active-point"
                                            stroke={group.color}
                                        />
                                    ) : null}
                                </g>
                            ))}
                            <circle cx={group.last.x} cy={group.last.y} r="5" fill="#fff" stroke={group.color} strokeWidth="2.5" />
                        </g>
                    ))}
                    {activeSelection ? (
                        <line
                            className="ai-dev-chart__crosshair"
                            data-testid="ai-dev-chart-crosshair"
                            x1={activeSelection.x}
                            x2={activeSelection.x}
                            y1={padding.top}
                            y2={padding.top + plotHeight}
                        />
                    ) : null}
                    {clusters.map((cluster) => {
                        const selected = !activeDate && activeClusterId === cluster.id;
                        const ariaLabelForCluster = cluster.members.length === 1
                            ? getPointAriaLabel(cluster.members[0].point, cluster.members[0].seriesLabel, valueFormatter)
                            : `${cluster.members.length} observations: ${cluster.members.map((member) => (
                                getPointAriaLabel(member.point, member.seriesLabel, valueFormatter)
                            )).join('; ')}`;
                        const selectCluster = () => {
                            setActiveDate(null);
                            setTooltipPosition(null);
                            setActiveClusterId(cluster.id);
                        };
                        const toggleCluster = () => {
                            setActiveDate(null);
                            setTooltipPosition(null);
                            setActiveClusterId((current) => current === cluster.id ? null : cluster.id);
                        };

                        return (
                            <g
                                key={cluster.id}
                                className="ai-dev-chart__hit-target"
                                role="button"
                                tabIndex={0}
                                aria-label={ariaLabelForCluster}
                                aria-describedby={selected ? tooltipId : undefined}
                                onPointerEnter={(event) => {
                                    if (!event.pointerType || event.pointerType === 'mouse') selectCluster();
                                }}
                                onPointerUp={(event) => {
                                    if (event.pointerType !== 'mouse') toggleCluster();
                                }}
                                onFocus={selectCluster}
                                onBlur={() => setActiveClusterId(null)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Escape') setActiveClusterId(null);
                                }}
                            >
                                {cluster.hitAreas.map((hitArea) => (
                                    <circle
                                        key={hitArea.id}
                                        cx={hitArea.x}
                                        cy={hitArea.y}
                                        r={hitArea.radius}
                                        className="ai-dev-chart__hit-area"
                                    />
                                ))}
                            </g>
                        );
                    })}
                </svg>
                {activeSelection ? (
                    <div
                        key={activeSelection.id}
                        ref={tooltipRef}
                        id={tooltipId}
                        role="tooltip"
                        className="ai-dev-chart__tooltip"
                        style={{
                            '--ai-tooltip-color': activeSelection.color,
                            left: tooltipPosition ? `${tooltipPosition.left}px` : 0,
                            top: tooltipPosition ? `${tooltipPosition.top}px` : 0,
                        }}
                    >
                        {activeSelection.members.length === 1 ? (
                            <>
                                <time>{activeSelection.members[0].point.date}</time>
                                <strong>{valueFormatter(activeSelection.members[0].point.value)}</strong>
                                {getTooltipDetails(
                                    activeSelection.members[0].point,
                                    activeSelection.members[0].seriesLabel,
                                    valueFormatter
                                ).map((detail) => (
                                    <span key={`${detail.label}-${detail.value}`}>
                                        <small>{detail.label}</small>
                                        {detail.value}
                                    </span>
                                ))}
                            </>
                        ) : (
                            <div className="ai-dev-chart__tooltip-members">
                                {activeSelection.members.map((member) => (
                                    <div className="ai-dev-chart__tooltip-member" key={member.id}>
                                        <time>{member.point.date}</time>
                                        <span>{getClusterMemberIdentity(member)}</span>
                                        <strong>{valueFormatter(member.point.value)}</strong>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default TimeSeriesChart;
